// src/pages/BillDetailPage.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  useMediaQuery,
  Alert,
  Chip,
  Divider,
  Grid,
  Stack,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { isMobile } from "react-device-detect";

const API_BASE = process.env.REACT_APP_API_URL;

const currency = (v) =>
  v ? Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "0.00";

const liters = (v) =>
  v ? Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 }) : "0.000";

const fmt = (isoDate) => {
  if (!isoDate) return "N/A";
  const [y, m, d] = String(isoDate).split("-");
  return `${d}/${m}/${y}`;
};

// -------- CollectionTable Component --------
function CollectionTable({ rows, title }) {
  if (!rows || rows.length === 0) return null;

  const totalQty = rows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);
  const totalAmt = rows.reduce((sum, r) => sum + (Number(r.baseamt) || 0), 0);

  const wAvg = (key) =>
    totalQty
      ? rows.reduce(
          (sum, r) =>
            sum + (Number(r.qty) || 0) * (Number(r[key]) || 0),
          0
        ) / totalQty
      : 0;

  return (
    <Box sx={{ p: 1 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, fontWeight: "bold", textAlign: "center" }}
      >
        {title}
      </Typography>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "13px",
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#f0f0f0" }}>
            <th style={th}>दिनांक</th>
            <th style={th}>प्रमाण (ली)</th>
            <th style={th}>फॅट</th>
            <th style={th}>SNF</th>
            <th style={th}>दर (₹)</th>
            <th style={th}>रक्कम (₹)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              style={{ backgroundColor: i % 2 === 0 ? "#fafafa" : "white" }}
            >
              <td style={td}>{fmt(r.tran_date)}</td>
              <td style={tdRight}>{liters(r.qty)}</td>
              <td style={tdRight}>{Number(r.fat || 0).toFixed(1)}</td>
              <td style={tdRight}>{Number(r.snf || 0).toFixed(1)}</td>
              <td style={tdRight}>{currency(r.rate)}</td>
              <td style={tdRight}>{currency(r.baseamt)}</td>
            </tr>
          ))}
          {/* Totals & Averages */}
          <tr style={{ backgroundColor: "#e8f5e9", fontWeight: "bold" }}>
            <td style={td}>एकूण</td>
            <td style={tdRight}>{liters(totalQty)}</td>
            <td style={tdRight}>{wAvg("fat").toFixed(1)}</td>
            <td style={tdRight}>{wAvg("snf").toFixed(1)}</td>
            <td style={tdRight}>{wAvg("rate").toFixed(2)}</td>
            <td style={tdRight}>{currency(totalAmt)}</td>
          </tr>
        </tbody>
      </table>
    </Box>
  );
}

const th = {
  padding: "6px",
  border: "1px solid #ccc",
  textAlign: "center",
};
const td = { padding: "6px", border: "1px solid rgba(7, 7, 7, 1)", textAlign: "left" };
const tdRight = { padding: "6px", border: "1px solid #040404ff", textAlign: "right" };

const naveTh = {
  padding: "6px",
  border: "1px solid #000",
  textAlign: "center",
};
const naveTd = {
  padding: "6px",
  border: "1px solid #000",
  textAlign: "left",
};
const naveTdRight = {
  padding: "6px",
  border: "1px solid #000",
  textAlign: "right",
};




// -------- Main Page Component --------
export default function BillDetailPage() {
  const { username, fromDate, toDate } = useParams();
  const navigate = useNavigate();
  const billRef = useRef();
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const [billData, setBillData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  console.log("BillData from state:", billData);

  useEffect(() => {
    const fetchBillDetails = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await axios.post(`${API_BASE}/bill-pdf-json`, {
          username,
          bill_fromdate: fromDate,
          bill_todate: toDate,
        });
        setBillData(response.data);
      } catch (e) {
        console.error("Error fetching bill details:", e);
        setError("बिल तपशील लोड करताना त्रुटी आली. कृपया पुन्हा प्रयत्न करा.");
      } finally {
        setLoading(false);
      }
    };

    fetchBillDetails();
  }, [username, fromDate, toDate]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          बिल लोड होत आहे...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          मागे जा
        </Button>
      </Box>
    );
  }

  if (!billData) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">बिल डेटा आढळला नाही.</Alert>
        <Button
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          मागे जा
        </Button>
      </Box>
    );
  }

  const { dairy = {}, bills = [], collections = [], nave_details =[] } = billData;
  const bill = bills[0] || {};
  const nave = nave_details[0] || {};
  console.log("Bill object from API:", bill);
  console.log("Nave object from API:", nave);
  // Group collections by milk type + shift
  const group = {};
  collections.forEach((c) => {
    const milkType = c.milktypecode === 1 ? "गाय" : "म्हैस";
    const shift = c.shift_code === 1 ? "सकाळ" : "संध्याकाळ";
    if (!group[milkType]) group[milkType] = { सकाळ: [], संध्याकाळ: [] };
    group[milkType][shift].push(c);
  });

  return (
    <Box sx={{ p: isSmallScreen ? 1 : 3, maxWidth: 1200, margin: "0 auto" }}>
      {/* Print Styles for A4 */}
      <style>
        {`
          @media print {
            @page {
              size: A4;
              margin: 10mm;
            }
            body {
              -webkit-print-color-adjust: exact;
              margin: 0;
              padding: 0;
            }
            .bill-content {
              width: 190mm;
              height: auto;
              overflow: hidden;
            }
          }
        `}
      </style>
      {/* Header with Back Button and Title */}
      <Box
        className="print-header"
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          मागे
        </Button>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          बिल तपशील
        </Typography>

        {!isMobile ? (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handlePrint}
          >
            PDF डाउनलोड करा
          </Button>
        ) : (
          <Alert severity="info" sx={{ maxWidth: 300 }}>
            PDF डाउनलोड करण्यासाठी, कृपया डेस्कटॉप किंवा लॅपटॉप वापरा.
          </Alert>
        )}
      </Box>

      {/* Bill Content */}
      <Paper elevation={2} sx={{ p: isSmallScreen ? 2 : 4 }} ref={billRef} className="bill-content">
        {/* Company Header */}
        
        {/* Farmer Information */}
        <Grid container spacing={3} sx={{ mb: 2, width: "100%"}}>
          <Grid item xs={12} md={6} style={{ border: "1px solid #1d1b1bff", padding: "10px", borderRadius: "4px", width: "30%" }}>
            <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: "bold", fontSize: isSmallScreen ? "1.1rem" : "1.5rem" }}
            >
                {dairy?.companyname || "डेअरी नाव"}
            </Typography>
            <Typography variant="body2">
                {[dairy?.Place, dairy?.Tal, dairy?.Dist, dairy?.Statename]
                .filter(Boolean)
                .join(", ") || "डेअरीचा पत्ता"}
            </Typography>
            <Chip
                label={`कालावधी: ${fmt(fromDate)} - ${fmt(toDate)}`}
                sx={{ mt: 1, fontWeight: "bold", backgroundColor: "white", color: "#4caf50" }}
            />
          </Grid>


          <Grid item xs={12} md={6} style={{ border: "1px solid #1d1b1bff", padding: "10px", borderRadius: "4px", width: "30%" }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: "bold" }}
            >
              उत्पादक
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>नाव:</strong> {bill.farmer_name || "N/A"} ({username})
              </Typography>
              <Typography variant="body2">
                <strong>मोबाइल:</strong> {bill.farmer_mobile || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>सेंटर:</strong> {bill.farmer_place || "N/A"}
              </Typography>

            </Stack>
          </Grid>

          <Grid item xs={12} md={6} style={{ border: "1px solid #1d1b1bff", padding: "10px", borderRadius: "4px", width: "auto" }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: "bold" }}
            >
              उत्पादकाचे बँक तपशील
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>बँक:</strong> {bill.bank_name || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>IFSC:</strong> {bill.ifsc || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>खाते क्रमांक:</strong> {bill.account_no || "N/A"}
              </Typography>
            </Stack>
          </Grid>

        </Grid>

        {/* Collections */}
        {Object.keys(group).map((milkType) => (
          <Box key={milkType} sx={{ width:"auto" }}>
            {/* <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", mb: 1, textAlign: "center", backgroundColor:"#c8e6c9", padding:1, borderRadius:1 }}
            >
              {milkType} – संकलन तपशील
            </Typography> */}
            <Grid container spacing={0} sx={{ border: "1px solid #050505ff", borderRadius: 1, overflow: "hidden" }}>
              <Grid item xs={12} md={6} sx={{ p: 0, width:"50%" }}>
                <CollectionTable
                  rows={group[milkType]["सकाळ"]}
                  title={`${milkType} – सकाळ`}
                  style ={{ backgroundColor:"#c8e6c9"}}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ p: 0, width:"50%"}}>
                <CollectionTable
                  rows={group[milkType]["संध्याकाळ"]}
                  title={`${milkType} – संध्याकाळ`}
                  style ={{ backgroundColor:"#c8e6c9"}}
                />
              </Grid>
            </Grid>
          </Box>
        ))}

        {/* Billing Summary */}
        <Box
          sx={{
            p: 1,
            backgroundColor: "#fffde7",
            borderRadius: 1,
            border: "1px solid #ccc",
          }}
        >
          {/* <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: "bold" }}
          >
            बिलिंग सारांश
          </Typography> */}
          <Grid container spacing={1}>
            <Grid item xs={12} md={6} style={{ border: "1px solid #1d1b1bff", padding: "5px", borderRadius: "4px", width: "auto" }}>
              <Typography 
                variant="body2" 
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <strong>गाय लिटर्स :</strong>
                <span>{currency(bill.cowqty)}</span>
              </Typography>
              <Typography 
                variant="body2"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <strong>म्हैस लिटर्स :</strong>
                <span>{currency(bill.buffqty)}</span>
              </Typography>
              <Typography 
                variant="body2"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <strong>एकूण लिटर्स :</strong>
                <span>
                  {currency(Number(bill.cowqty || 0) + Number(bill.buffqty || 0))}
                </span>

              </Typography>

              <Typography 
                variant="body2" 
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <strong>गाय रक्कम :</strong>
                <span> ₹ {currency(bill.cowamt)}</span>
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <strong>म्हैस रक्कम :</strong>
                <span> ₹ {currency(bill.buffamt)}</span>
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <strong>ए. रक्कम:</strong>
                <span>
                  ₹ {currency(Number(bill.cowamt || 0) + Number(bill.buffamt || 0))}
                </span>

              </Typography>
                            
            </Grid>

            <Grid item xs={12} md={6} style={{ border: "1px solid #1d1b1bff", padding: "10px", borderRadius: "4px", width: "auto" }}>


              <Typography 
                variant="body2" 
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <strong>गाय व्यवस्थापन :</strong>
                <span> ₹ {currency(bill.cowcommissionamt)}</span>
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <strong>म्हैस व्यवस्थापन :</strong>
                <span> ₹ {currency(bill.buffcommissionamt)}</span>
              </Typography>

            </Grid>
            <Grid item xs={12} md={6} style={{ border: "1px solid #1d1b1bff", padding: "10px", borderRadius: "4px", width: "auto" }}>

              {/* <Typography 
                variant="body2" 
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <strong>गाय व्यवस्थापन :</strong>
                <span> ₹ {currency(bill.cowcommissionamt)}</span>
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <strong>म्हैस व्यवस्थापन :</strong>
                <span> ₹ {currency(bill.buffcommissionamt)}</span>
              </Typography> */}
              <Typography 
                variant="body2" 
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <strong>एकूण देय :</strong>
                <span> ₹ {currency(bill.EkunKharedi)}</span>
              </Typography>
              <Typography variant="body2">
                <strong>एकूण कपात :</strong> ₹{currency(bill.EkunNave)}
              </Typography>
            </Grid>

          <Box sx={{ mt: 0 }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f0f0f0" }}>
                  <th style={naveTh}>नावे तपशील</th>
                  <th style={naveTh}>मागील बाकी</th>
                  <th style={naveTh}>चालू नावे</th>
                  <th style={naveTh}>कपात (जमा)</th>
                  <th style={naveTh}>शिल्लक बाकी</th>
                </tr>
              </thead>
              <thead>
                <tr style={{ backgroundColor: "#f0f0f0" }}>
                  <th style={naveTh}>औषध</th>
                  <td style={naveTdRight}>0.00</td>
                  <td style={naveTdRight}>0.00</td>
                  <td style={naveTdRight}>0.00</td>
                  <td style={naveTdRight}>0.00</td>
                </tr>
              </thead>
              <thead>
                <tr style={{ backgroundColor: "#f0f0f0" }}>
                  <th style={naveTh}>ए. आय.</th>
                  <td style={naveTdRight}>0.00</td>
                  <td style={naveTdRight}>0.00</td>
                  <td style={naveTdRight}>0.00</td>
                  <td style={naveTdRight}>0.00</td>
                </tr>
              </thead>              
              <thead>
                <tr style={{ backgroundColor: "#f0f0f0" }}>
                  <th style={naveTh}>पशूखाद्य</th>
                  <td style={naveTdRight}>{currency(nave.preuchal2)}</td>
                  <td style={naveTdRight}>{currency(nave.uchalplus2)}</td>
                  <td style={naveTdRight}>{currency(nave.uchalminus2)}</td>
                  <td style={naveTdRight}>{currency(nave.curruchal2)}</td>
                </tr>
              </thead>              
              <thead>
                <tr style={{ backgroundColor: "#f0f0f0" }}>
                  <th style={naveTh}>अडवांस</th>
                  <td style={naveTdRight}>{currency(nave.preuchal3)}</td>
                  <td style={naveTdRight}>{currency(nave.uchalplus3)}</td>
                  <td style={naveTdRight}>{currency(nave.uchalminus3)}</td>
                  <td style={naveTdRight}>{currency(nave.curruchal3)}</td>                </tr>
              </thead>
            </table>
          </Box>


          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "primary.main" }}
            >
              देय रक्कम: ₹{currency(bill.NetBill)}
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        {/* <Typography
          variant="caption"
          display="block"
          sx={{ mt: 3, textAlign: "center", color: "text.secondary" }}
        >
          हे बिल संगणकाद्वारे स्वयंचलितपणे तयार झाले आहे.
        </Typography> */}
      </Paper>

      {/* {isMobile && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>सूचना:</strong> PDF डाउनलोड करण्यासाठी, कृपया ही पृष्ठ डेस्कटॉप
          किंवा लॅपटॉप कॉम्प्युटरवर उघडा.
        </Alert>
      )} */}
    </Box>
  );
}
